// poiesis/src/views/engine.tsx
// Builds canonical ReduceRequest (T2) and hosts view rendering via React Query (T4).
// ViewHost requires a QueryClient in context (from App.tsx or test).
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { chunkRequest, chunkWindows, mergeChunkFrames, visibleRows, type TimeWindow } from './chunks'
import { repeatableBindings } from './roleSlots'
import { getTenantZone, todayInZone, zonedMidnightUtc } from '../locale' 
import { resolveViewRenderer, rendererOptions } from './registry'
import type { ViewKindSpec } from './catalog'
import type { FilterClause, ReduceRequest, ReducedFrame, Reducer, Scope } from './contract'
import type { Granularity } from '../api/contract.generated'
import { reduce } from '../api/reduce'
import { attributesQuery } from '../api/typeMetadata'
import { monadClient, type DashboardWidget, type RecordGroup, type RecordsQuestion, type WidgetBind } from '../api/client'
import { applicationFor, composeFilter, type MasterApplication, type WidgetAttr, type WireTree } from '../dashboards/masterFilter'
import type { AskQuestion } from './registry'
import { attachRecordColors, attachSetChips, chipIdsIn, UNNAMED } from './seriesColor'
import { FilteredEmptyState } from './FilteredEmptyState'
import type { FrameMark } from './contract'
import type { Density, DensityPolicy } from './catalog'
import { FrameAnnotations } from './FrameAnnotations'
import type { SelectionSet } from '../collection/selection'

// ---------------------------------------------------------------------------
// Default period — the window every view and dashboard widget reduces over
// when nothing chose one. Trailing 90 days ending today (UTC midnight).
//
// This replaced a constant pinned to January 2024 "for the seed data". On the
// jysk tenant with real telemetry (May–Sep 2026) that constant made every
// chart widget render "No data" while the reduce endpoint had the rows — the
// same silently-empty class as SEN-1719, one layer up. A period that tracks
// today is still not a period the USER chose (that is SEN-1720: a selector,
// fiscal-year aware via tenants.fiscal_year_start_month); it is the honest
// default until then, and this function is the one seam that selector replaces.
// ---------------------------------------------------------------------------
export const DEFAULT_PERIOD_DAYS = 90

export function defaultPeriod(now: Date = new Date(), tz: string = getTenantZone()): { period_start: string; period_end: string } {
  // SEN-1761: last LOCAL midnight back 90 local days — the server default
  // (period_binding.resolve_period(None, now, tz)), not a UTC window.
  const { y, m, d } = todayInZone(tz, now)
  return {
    period_start: zonedMidnightUtc(tz, y, m, d - DEFAULT_PERIOD_DAYS).toISOString(),
    period_end: zonedMidnightUtc(tz, y, m, d).toISOString(),
  }
}


// ---------------------------------------------------------------------------
// SEN-1732: THE effective measured quantity — one function, one answer.
//
// Sites → line showed a `y` chip reading `actual_hdd` while the request
// measured something else. Nothing chose `actual_hdd`: the chip is a controlled
// <select> whose `value` came from App state that no one had grounded in the
// entity's quantity catalog (the app-wide default 'kwh'; the site catalog has
// no 'kwh'), so the DOM silently rendered selectedIndex 0 — the
// alphabetically-first quantity. A chip naming a quantity the request does not
// carry is a lie (truthful affordances), so the chip and `scope.quantity` must
// be the SAME expression, not two expressions that happen to agree.
//
// Precedence — the SEN-706 single-quantity contract, stated once:
//   1. a quantity bound to a ROLE takes over scope.quantity
//   2. otherwise the selected quantity
// …then GROUNDED: a slug the entity's catalog does not offer is not a measure
// this entity has, so the honest answer is "none" ('') rather than a phantom
// slug on the wire. The server annotates a quantity-less reduce
// ("Scope has no telemetry `quantity` … No rows produced") rather than
// refusing it — annotate, don't block.
//
// An absent or EMPTY catalog grounds nothing: "not loaded yet" and "the fetch
// failed" are indistinguishable from "this entity measures nothing", so the
// selection passes through unchanged rather than being silently blanked.
// ---------------------------------------------------------------------------
export interface QuantityChoice { slug: string; display_name: string }

export function effectiveMeasureQuantity(
  selected: string | undefined,
  quantityBindings: Record<string, string> = {},
  catalog?: readonly QuantityChoice[],
): string {
  const picked = Object.values(quantityBindings)[0] ?? selected ?? ''
  if (!picked) return ''
  if (!catalog || catalog.length === 0) return picked
  return catalog.some((q) => q.slug === picked) ? picked : ''
}

/** SEN-1732: the human name for a quantity slug. Never a UUID, and never a bare
 *  slug when the catalog carries a display name for it. A slug with no catalog
 *  entry reads as itself — the honest fallback, since inventing a prettier name
 *  would be the same fabrication this ticket removes. */
export function quantityLabel(slug: string, catalog: readonly QuantityChoice[] = []): string {
  return catalog.find((q) => q.slug === slug)?.display_name || slug
}

/** Does this KIND consume a telemetry quantity at all?
 *
 *  A quantity belongs in scope ONLY when the kind declares a role that consumes
 *  one (reduce roles: bar/line/histogram/pivot/map measure; or
 *  substrate-satisfiable axes: scatter). Board/gantt/tree identity frames are
 *  CELL reads — the caught live bug (SEN-1409): the app's default 'kwh' leaked
 *  into an Initiative kanban, so the frame held TELEMETRY entities (sites,
 *  meters) and every group/label column resolved for none of them.
 *
 *  SEN-1732 cold read #3: this is a property of the KIND, not of whatever value
 *  happens to be resolved right now. App's period chip used to key off the
 *  resolved `effectiveQuantity`, so grounding made the chip flicker off/on
 *  across the pre-seed window and stay hidden for a view whose stored
 *  measureQuantity is not in the catalog — a period the view HAS, hidden
 *  because a different field was unresolved. Both the wire and the chip now ask
 *  the same structural question. */
export function kindWantsQuantity(spec: ViewKindSpec): boolean {
  return spec.roles.some((r) => r.reduce || r.substrate_satisfiable)
}

// SEN-1428: attribute kinds that mark a role as the TIME AXIS of a time-bucket
// kind (the axis is the bucket dimension itself, never a series dim). Mirrors
// the temporal accepts of the served catalog's time roles
// (monad/view_kinds_catalog/line.py: date/timestamp/time).
const TEMPORAL_ACCEPTS = new Set(['date', 'datetime', 'timestamp', 'time'])

// ---------------------------------------------------------------------------
// buildReduceRequest
// Emits a canonical tagged ReduceRequest from the view spec + bindings.
//
// Parameters:
//   spec           ViewKindSpec from the catalog
//   entityDef      entity_def string (e.g. "site")
//   roleNames      map: roleName → attribute STRING NAME (e.g. "group" → "building_type")
//                  Resolves each bound non-reduce role's leaf to its attribute name.
//                  (Gap 2: GroupBy.dimensions are attribute names, not UUIDs.)
//   measureQuantity quantity string for scope (e.g. "kwh"); compiled from the measure role.
//                  (Gap 1: scope.quantity replaces measure.path on the wire.)
//   agg            aggregation function (default "sum")
//   granularity    for time-bucket reducers
//   period         { period_start, period_end } ISO strings; defaults to defaultPeriod()
// ---------------------------------------------------------------------------
export function buildReduceRequest(
  spec: ViewKindSpec,
  entityDef: string,
  roleNames: Record<string, string>,
  measureQuantity: string | undefined,
  agg: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last' = 'sum',
  granularity: Granularity = 'day',
  period?: { period_start: string; period_end: string },
  filter?: unknown,
  secondMeasure?: string,
  config?: Record<string, unknown>,
): ReduceRequest {
  const effectivePeriod = period ?? defaultPeriod()

  // GAP B: a `table` is an IDENTITY kind, but a RANK declares a time-bucket
  // frame ("the ten biggest hours"). The declaration is the config's, so the
  // fork is metadata, not a kind name.
  const rank = (config ?? {}).rank as { by: string; n: number; order: 'asc' | 'desc' } | undefined
  // SEN-1756: the config's own bucket size — the rank fork's bucket AND a
  // time-bucket kind's Bucket control (both gated by the registry's
  // _granularity_error; anything else 422s at save).
  const configGranularity = (config ?? {}).granularity as Granularity | undefined

  const wantsQuantity = kindWantsQuantity(spec) || Boolean((config ?? {}).rank)

  // SEN-1513 half 2: the TWO-measure form. When the kind's reduce role is
  // repeatable (line's `y`) and a second measure is bound, BOTH go on the wire
  // as `scope.quantities` — one request, ONE server-side frame. The server
  // gates the pair (time-bucket only, no series dim, unit agreement) and
  // annotates why when it refuses; the client never stitches two frames.
  // The cap is 2 at the contract; equal slugs are not a second measure.
  //
  // SEN-1732 cold read #2: the PRIMARY must be truthy. App seeds the second
  // measure (`config.measureQuantities[1]`) independently of whether the
  // primary seed applied, so a rejected primary used to emit
  // `quantities: ['', 'net_kwh']`. The server's `_effective_quantities` then
  // takes the pair branch and resolves '' instead of raising its honest
  // "Scope has no telemetry `quantity`" warning — a silent wrong answer
  // replacing a truthful refusal. A second measure with no first is not a pair.
  const repeatableReduceRole = spec.roles.find((r) => r.reduce && r.arity === 'repeatable')
  const twoMeasures = Boolean(
    repeatableReduceRole && wantsQuantity && measureQuantity
    && secondMeasure && secondMeasure !== measureQuantity,
  )

  const scope: Scope = {
    entity_def: entityDef,
    ...(wantsQuantity && twoMeasures
      ? { quantities: [measureQuantity as string, secondMeasure as string] }
      : wantsQuantity && measureQuantity ? { quantity: measureQuantity } : {}),
    period_start: effectivePeriod.period_start,
    period_end: effectivePeriod.period_end,
    ...(filter !== undefined ? { filter: filter as Scope['filter'] } : {}),
    // GAP A: a comparison is a WINDOW binding, so it rides the scope. Only a
    // scalar kind can answer it (the server annotates otherwise) — the client
    // does not put a request on the wire it knows will be refused.
    ...(spec.default_reducer === 'scalar' && (config ?? {}).compare
      ? { compare: (config as Record<string, unknown>).compare as Scope['compare'] }
      : {}),
  }

  // Collect bound non-reduce dimension roles → attribute NAME strings.
  // Repeatable roles may have multiple slots: base key `R` plus `R::1`, `R::2`, etc.
  // Roles with `consumes_subfields` expand to "<boundSlug>.<sub>" for each declared
  // sub-field instead of the bare slug — generic, metadata-driven, no per-slug branch.
  // The dot-join convention "<slug>.<sub>" is owned by
  // monad/aion/dim_explode.py::explode_dim_rows (f"{attribute}.{sub.name}")
  // — this expansion MUST match that convention exactly (drift guard, audit Q2a).
  const dimNames: string[] = spec.roles
    .filter((r) => !r.reduce)
    .flatMap((r) => {
      if (r.arity === 'repeatable') {
        // TODO (M3): repeatable roles with consumes_subfields are not expanded (no such role today)
        return repeatableBindings(roleNames, r.name)
      }
      if (r.interval) {
        // SEN-1410: an interval role binds a START/END PAIR — slots keyed
        // r.name + `${r.name}::end` (repeatable-slot convention). Collect both.
        return [roleNames[r.name], roleNames[`${r.name}::end`]].filter((x): x is string => Boolean(x))
      }
      const boundSlug = roleNames[r.name]
      // A grouping role may be bound to a cyclic time part by NAME — the
      // literal token the resolver's grammar accepts. This is how "consumption
      // by weekday" is expressed on a bar: roleNames.group = 'time-part(weekday)'.
      if (boundSlug && /^time-part\(/.test(boundSlug)) return [boundSlug]
      if (r.time_part) {
        // Substrate-satisfied cyclic axis (heatmap): a bound categorical
        // attribute wins; otherwise the part named by "<role>_part", else the
        // role's default. Mirrors monad/metrics/resolver.py _PART_EXPR.
        if (boundSlug) return [boundSlug]
        const opt = (config ?? {})[`${r.name}_part`]
        return [`time-part(${typeof opt === 'string' && opt ? opt : r.time_part})`]
      }
      if (!boundSlug) return []
      if (r.consumes_subfields && r.consumes_subfields.length > 0) {
        // Composite role: expand to one "<boundSlug>.<sub>" column per declared sub-field.
        return r.consumes_subfields.map((sub) => `${boundSlug}.${sub}`)
      }
      return [boundSlug]
    })
    .filter(Boolean)

  if (rank && spec.default_reducer === 'identity') {
    return {
      op: 'reduce',
      scope,
      reducer: {
        kind: 'time-bucket',
        agg: (agg === 'count' ? 'sum' : agg) as 'sum' | 'avg' | 'min' | 'max' | 'first' | 'last',
        granularity: configGranularity ?? granularity,
        rank,
      },
      mrl_snapshot_id: null,
      accurate_at: null,
    }
  }

  let reducer: Reducer
  switch (spec.default_reducer) {
    case 'time-bucket': {
      // SEN-1428: the series dim. The kind's TIME AXIS role is consumed by the
      // bucket dimension itself — identified by its temporal `accepts` (metadata,
      // no role-name literal); every OTHER bound non-reduce role (line's `series`)
      // becomes a reducer dimension. Max one renders in v1; the reducer
      // annotates-not-blocks beyond that, so no client-side silent slice.
      const seriesDims = spec.roles
        .filter((r) => !r.reduce && !r.accepts.some((a) => TEMPORAL_ACCEPTS.has(a)))
        .map((r) => roleNames[r.name])
        .filter((x): x is string => Boolean(x))
      // count is invalid for time-bucket BucketAgg — fall back to sum
      reducer = {
        kind: 'time-bucket',
        agg: (agg === 'count' ? 'sum' : agg) as 'sum' | 'avg' | 'min' | 'max' | 'first' | 'last',
        // SEN-1756: a widget's own config.granularity wins (the structural
        // Bucket control; a dashboard tile has no strip to bind one) — the
        // caller-passed prop is the view surface's live binding, and a view
        // config never persists granularity, so the two can't conflict.
        granularity: configGranularity ?? granularity,
        ...(seriesDims.length > 0 ? { dimensions: seriesDims } : {}),
      }
      break
    }
    case 'group-by':
      // heatmap scale_by: the ramp's domain is per group, so the group is a
      // trailing dimension and the renderer facets on it. "all" = no facet.
      {
        const scaleBy = (config ?? {}).scale_by
        if (spec.config_schema?.some((o) => o.key === 'scale_by') && typeof scaleBy === 'string' && scaleBy && scaleBy !== 'all' && !dimNames.includes(scaleBy)) {
          dimNames.push(scaleBy)
        }
        // 2026-10-23: a declared `outline_part` asks the engine for the time part each
        // cell falls in (operating_zone), so the outline is the engine's classification
        const outline = (config ?? {}).outline_part
        const part = typeof outline === 'string' && outline !== 'none' ? `time-part(${outline})` : null
        if (spec.config_schema?.some((o) => o.key === 'outline_part') && part && !dimNames.includes(part)) {
          dimNames.push(part)
        }
      }
      if (dimNames.length === 0) {
        // No dimensions bound → fall back to scalar (avoid invalid empty-dimensions/422)
        reducer = { kind: 'scalar', agg }
      } else {
        // dimNames is non-empty here; the generated `Dimensions` is a min-1 tuple
        // ([string, ...string[]]) — codegen artifact of minItems:1. Safe narrowing.
        reducer = { kind: 'group-by', agg, dimensions: dimNames as [string, ...string[]] }
      }
      break
    case 'scalar':
      // 2026-10-23: a scalar kind declared `over_time` reads its measure over
      // time when the config names a granularity — the renderer draws the
      // LATEST bucket (a "current" reading; a scalar cannot fold in time order).
      reducer = spec.over_time && configGranularity
        ? { kind: 'time-bucket', agg: (agg === 'count' ? 'sum' : agg) as 'sum' | 'avg' | 'min' | 'max' | 'first' | 'last', granularity: configGranularity }
        : { kind: 'scalar', agg }
      break
    case 'identity':
    default: {
      // SEN-706 / SEN-1721: an identity request that names columns REPLACES the
      // reducer's default `[quantity]` (documented contract) — so a kind with a
      // reduce role (the map's `measure`) must ask for its measure column too,
      // or the frame comes back without the very value it sizes by. The
      // column is the bound quantity slug (quantityBindings) or, unbound, the
      // scope quantity; kinds without a reduce role (kanban, calendar …) add
      // nothing — an engine resolve per entity they would never read.
      const reduceCols = spec.roles.some((r) => r.reduce)
        ? (() => {
            const bound = spec.roles.filter((r) => r.reduce).map((r) => roleNames[r.name]).filter((x): x is string => Boolean(x))
            return bound.length > 0 ? bound : measureQuantity ? [measureQuantity] : []
          })()
        : []
      const columns = [...new Set([...dimNames, ...reduceCols])]
      reducer = { kind: 'identity', columns: dimNames.length > 0 ? columns : null }
      break
    }
  }

  return {
    op: 'reduce',
    scope,
    reducer,
    mrl_snapshot_id: null,
    accurate_at: null,
  }
}

// ---------------------------------------------------------------------------
// buildQuestions — THE widget → question compile (dashboard spec §4.4).
//
// A widget asks up to two questions: the kind's REDUCE (buildReduceRequest,
// the internal step) and the RECORDS question of its `bind.records` (PR 406's
// records binding: count and area are record facts, not telemetry). Both are
// keyed by request in react-query and coalesced into the same /batch.
//
//   - a scalar with `compare` goes in the `measures:[{as, metric}]` form: that
//     form takes the engine's union-baseline path (reduce.py _reduce_compared,
//     proven by tests/gateway/test_dashboard_parity.py) — the previous window
//     asked over the SAME scope, not pinned to the primary window's entities;
//   - `measures` (ask(), e.g. a map popup) is a scalar over those metrics under
//     the widget's scope — the same compile, never a second one;
//   - ONE generic rule (A6): a widget with `bind` satisfies its kind's
//     required reduce roles from the binding — when none is bound, no reduce
//     is asked (a records-only stat).
// The master filter composes onto BOTH questions' filters (composeFilter).
// ---------------------------------------------------------------------------
export interface WidgetQuestions {
  reduce?: ReduceRequest
  /** `object` is the widget type's slug (the records route takes a slug or an id). */
  records?: { object: string; query: RecordsQuestion }
}

export interface QuestionInput {
  spec: ViewKindSpec
  /** the widget type's slug — the reduce scope's entity_def and the records route's object */
  entityDef: string
  roleNames?: Record<string, string>
  measureQuantity?: string
  secondMeasure?: string
  config?: Record<string, unknown>
  bind?: WidgetBind | null
  period?: { period_start: string; period_end: string }
  /** the widget's OWN filter (config.filter) — the reduce question's */
  filter?: unknown
  /** the host's scope as applied to this widget (master filter / record binding) */
  master?: WireTree
  agg?: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last'
  granularity?: Granularity
  /** ask(): a scalar over these metric slugs instead of the kind's own reduce */
  measures?: string[]
  /** ask(): the unit those measures are answered in (the server converts — Measure.unit) */
  measuresUnit?: string
}

/** A column name for a metric slug in the `measures` form (`as` is ^[a-z][a-z0-9_]*$). */
export function measureColumn(slug: string): string {
  const s = slug.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^[^a-z]+/, '')
  return (s || 'm').slice(0, 63)
}

export function buildQuestions(input: QuestionInput): WidgetQuestions {
  const { spec, entityDef, config = {}, bind, master } = input
  const roleNames = input.roleNames ?? {}
  const out: WidgetQuestions = {}
  const reduceFilter = composeFilter(input.filter, master)

  if (input.measures && input.measures.length > 0) {
    const base = buildReduceRequest(spec, entityDef, roleNames, undefined, input.agg, input.granularity,
      input.period, reduceFilter, undefined, { compare: config.compare })
    const { quantity: _q, quantities: _qs, ...scope } = base.scope
    out.reduce = {
      ...base,
      scope: { ...scope, ...(config.compare ? { compare: config.compare as Scope['compare'] } : {}) },
      reducer: { kind: 'scalar', agg: input.agg ?? 'sum' },
      measures: [...new Set(input.measures)].map((m) => ({ as: measureColumn(m), metric: m, ...(input.measuresUnit ? { unit: input.measuresUnit } : {}) })),
    } as ReduceRequest
    return out
  }

  const records = bind?.records
  if (records) {
    const query: RecordsQuestion = {}
    const filter = composeFilter(records.filter ?? undefined, master)
    if (filter !== undefined && filter !== null) query.filter = filter as FilterClause
    if (records.group_by && records.group_by.length > 0) { query.group_by = records.group_by; query.limit = 200 }
    if (records.aggregates && records.aggregates.length > 0) query.aggregates = records.aggregates
    // the window a CALCULATED filter leaf is compared over (ignored otherwise)
    if (input.period) { query.period_start = input.period.period_start; query.period_end = input.period.period_end }
    out.records = { object: entityDef, query }
  }

  const measure = input.measureQuantity
    || spec.roles.filter((r) => r.reduce).map((r) => roleNames[r.name]).find(Boolean)
    || ''
  const unboundRequiredMeasure = spec.roles.some((r) => r.reduce && r.required) && !measure
  if (records && unboundRequiredMeasure) return out

  const req = buildReduceRequest(spec, entityDef, roleNames, measure, input.agg, input.granularity,
    input.period, reduceFilter, input.secondMeasure, config)
  if (req.reducer.kind === 'scalar' && req.scope.compare && req.scope.quantity) {
    const { quantity, ...scope } = req.scope
    out.reduce = { ...req, scope, measures: [{ as: 'value', metric: quantity }] } as ReduceRequest
  } else {
    out.reduce = req
  }
  return out
}

/** The per-widget assembly every host shares (A10): the dashboard's tile, a
 *  record page's widget and the dashboard prefetch. It translates the host's
 *  scope for THIS widget's type (applicationFor: as-is, through a forward
 *  reference, n/a, ignored, or pending while the type's attributes load), then
 *  compiles through buildQuestions. `application` lets the host draw the truth
 *  about the scope (the n/a badge); `pending` asks nothing yet. */
export function widgetQuestions(
  widget: Pick<DashboardWidget, 'view_kind' | 'type_id' | 'config' | 'bind'>,
  ctx: {
    spec: ViewKindSpec
    typeSlug: string
    masterWire?: WireTree
    pivotTypeId?: string
    widgetAttrs?: WidgetAttr[]
    period?: { period_start: string; period_end: string }
    measures?: string[]
  },
): { application: MasterApplication; questions: WidgetQuestions | null } {
  const config = (widget.config ?? {}) as Record<string, unknown>
  const application = widget.type_id
    ? applicationFor(config, ctx.masterWire, ctx.pivotTypeId, widget.type_id, ctx.widgetAttrs)
    : ({ kind: 'none' } as const)
  if (application.kind === 'pending') return { application, questions: null }
  return {
    application,
    questions: buildQuestions({
      spec: ctx.spec,
      entityDef: ctx.typeSlug,
      roleNames: (config.roleNames as Record<string, string> | undefined) ?? {},
      measureQuantity: typeof config.measureQuantity === 'string' ? config.measureQuantity : undefined,
      secondMeasure: typeof config.secondMeasure === 'string' ? config.secondMeasure : undefined,
      config,
      bind: widget.bind,
      period: ctx.period,
      filter: config.filter,
      master: application.kind === 'apply' ? application.tree : undefined,
      measures: ctx.measures,
    }),
  }
}

// ---------------------------------------------------------------------------
// SEN-699: reference-valued dimensions resolve to kennings before rendering.
// The engine groups entity-reference dims by target record_id; raw ids must
// never reach a renderer (never-show-UUID, substrate invariant). Resolution is
// metadata-driven: the caller declares which dims are references (refDims:
// dim string → target type_id), kennings come from the target type's MRL, and
// the remap happens HERE once for every frame-mode renderer (cartesian, board,
// hierarchy, pivot, geo) — no per-renderer label hacks.
// ---------------------------------------------------------------------------
const KENNING_FETCH_LIMIT = 1000

/** records/values' answer ({record id: {attribute id: value}}) re-keyed by the attributes' SLUGS (a template's
 *  names); a key that is already a slug (a dotted path token) is kept as it is. */
export function cellsBySlug(
  values: Record<string, Record<string, unknown>>,
  attrs: ReadonlyArray<{ attribute_id: string; slug: string }>,
): Record<string, Record<string, unknown>> {
  const slugOf = new Map(attrs.map((a) => [a.attribute_id, a.slug]))
  return Object.fromEntries(Object.entries(values).map(([id, cells]) => [id,
    Object.fromEntries(Object.entries(cells ?? {}).map(([k, v]) => [slugOf.get(k) ?? k, v]))]))
}

export function remapReferenceDims<T extends { rows: Record<string, unknown>[] }>(
  frame: T,
  kenningsByDim: Record<string, Record<string, string>>,
  // Return type widens rows: the remap ADDS `<dim>__ref` keys the input type
  // doesn't carry — the signature must say so (verify-read type-lie fix).
): Omit<T, 'rows'> & { rows: Record<string, unknown>[] } {
  const dims = Object.keys(kenningsByDim)
  if (dims.length === 0) return frame
  return {
    ...frame,
    rows: frame.rows.map((row) => {
      const next = { ...row }
      for (const dim of dims) {
        const v = next[dim]
        // A declared reference dim NEVER renders a raw id — unmatched ids
        // (kenning fetch truncated, or record without a kenning) degrade to
        // '(unnamed)', not to the UUID. The RAW id survives in a side channel
        // (`<dim>__ref`) that renderers emit as the onSelect rowKey and never
        // display — the record-peek default needs the id, the eye never does
        // (cold-read verify: the kenning remap made the UUID-gated default
        // unreachable).
        if (typeof v === 'string') {
          next[`${dim}__ref`] = v
          next[dim] = kenningsByDim[dim][v] ?? '(unnamed)'
        }
      }
      return next
    }),
  }
}

// ---------------------------------------------------------------------------
// SEN-1410: identity frames key rows by BOUND ATTRIBUTE SLUGS (the reducer's
// columns), but the frame renderers' contract keys rows by ROLE names — the
// shape the gallery mock always produced (group/label/start/end, mock.ts).
// Nothing translated between the two, because until the F1 un-nerf no real
// identity frame had ever reached those renderers. This is the ONE generic
// translation seam: spec.roles × roleNames, no per-kind branch.
//   - non-reduce role R bound to slug S      → row[R] = row[S]
//   - interval role I (slots I, `I::end`)    → row.start / row.end
//     ("start"/"end" ARE the interval layout contract — GanttRenderer)
//   - entity_ref (reduce.py _ENTITY_REF_KEY) → row.id (renderer row identity)
// Existing keys are never clobbered (mock frames are already role-keyed).
// ---------------------------------------------------------------------------
export function remapRoleColumns<T extends { rows: Record<string, unknown>[] }>(
  frame: T,
  spec: ViewKindSpec,
  roleNames: Record<string, string>,
): T {
  const copies: Array<[from: string, to: string]> = []
  for (const r of spec.roles) {
    if (r.reduce) continue
    if (r.interval) {
      const s = roleNames[r.name]
      const e = roleNames[`${r.name}::end`]
      if (s) copies.push([s, 'start'])
      if (e) copies.push([e, 'end'])
      continue
    }
    const slug = roleNames[r.name]
    if (slug && slug !== r.name) copies.push([slug, r.name])
  }
  const needsId = frame.rows.some((row) => row.id === undefined && row.entity_ref !== undefined)
  if (copies.length === 0 && !needsId) return frame
  return {
    ...frame,
    rows: frame.rows.map((row) => {
      const next = { ...row }
      if (next.id === undefined && next.entity_ref !== undefined) next.id = next.entity_ref
      for (const [from, to] of copies) {
        if (next[to] !== undefined || next[from] === undefined) continue
        if (Array.isArray(next[from])) {
          // SEN-1733 cold read 3: a SET column holds raw record ids. Copying
          // that array onto the role key hands any generic role-keyed surface
          // a list of UUIDs to print. The chips carry the labels — copy those.
          // No chips attached yet → leave the role key UNSET: a renderer that
          // shows nothing is honest; one that shows UUIDs is not.
          const chips = next[`${from}__chips`]
          if (Array.isArray(chips)) {
            next[to] = (chips as { kenning: string }[]).map((c) => c.kenning)
          }
          continue
        }
        next[to] = next[from]
      }
      return next
    }),
  }
}

// ---------------------------------------------------------------------------
// ViewHost — renders a view asynchronously via React Query.
// Requires a QueryClientProvider in the component tree above.
// Table branch keeps the identity (collection) path unchanged.
// Non-table branches call reduce() and render the frame.
// ---------------------------------------------------------------------------
/** Everything ViewHost layers onto a reduced frame before a renderer sees
 *  it, in the one order that keeps every layer: set-column chips (SEN-1733)
 *  first, then the reference-dim kenning remap (which keeps ids in `__ref`),
 *  then record colours, then the label-cap annotation. Pure — the hook above
 *  only supplies the fetched inputs. */
export function decorateFrame<T extends { rows: Record<string, unknown>[]; meta?: any }>(
  frame0: T,
  inputs: {
    refDims: Record<string, string>
    setColumns: string[]
    chips: Record<string, { kenning: string; icon: string | null; color: string | null }> | undefined
    kenningsByType: Record<string, { kennings: Record<string, string>; total: number }> | undefined
    /** SEN-1733 cold read 4: the chip request failed outright (the query's
     *  isError). Every marker would silently degrade to a plain dot; the frame
     *  says so instead. */
    chipsFailed?: boolean
    /** Own-row identity columns whose UUID values need the same chip seam. */
    identityColumns?: string[]
  },
): T {
  const { refDims, setColumns, chips, kenningsByType, chipsFailed, identityColumns = [] } = inputs
  // the layers return structurally-equal frames; the widening is local
  let shown = frame0 as { rows: Record<string, unknown>[]; meta?: any }
  // Chips for every column that carries record ids: the set columns AND the
  // reference dims (a single reference is a one-chip stack). Before the
  // kenning remap, while the raw ids are still in place.
  const chipColumns = [...new Set([...setColumns, ...Object.keys(refDims), ...identityColumns])]
  if (chipColumns.length > 0 && chips) {
    shown = attachSetChips(shown, chipColumns, chips)
  }
  // no-silent-caps (cold read 3): an id the chip seam did not return renders
  // as '(unnamed)'. Count the DISTINCT ones and say so once — a legend of
  // unnamed circles with nothing explaining them is a silent cap.
  if (chipColumns.length > 0 && chips) {
    const unresolved = new Set<string>()
    for (const row of shown.rows) {
      for (const col of chipColumns) {
        for (const id of chipIdsIn(row, col) ?? []) if (!chips[id]) unresolved.add(id)
      }
    }
    if (unresolved.size > 0) {
      shown = _withWarning(
        shown,
        `${unresolved.size} referenced record${unresolved.size === 1 ? '' : 's'} `
        + `${unresolved.size === 1 ? 'has' : 'have'} no label yet and show as '${UNNAMED}'.`,
      )
    }
  }
  if (chipsFailed) {
    shown = _withWarning(
      shown,
      'Referenced records could not be resolved to labels, icons and colours — '
      + 'the chip request failed. Markers draw without them.',
    )
  }
  if (kenningsByType) {
    const kenningsByDim = Object.fromEntries(
      Object.entries(refDims).map(([dim, tid]) => [dim, kenningsByType[tid]?.kennings ?? {}]),
    )
    shown = remapReferenceDims(shown, kenningsByDim)
    if (chips) {
      const colorsByDim = Object.fromEntries(
        Object.keys(refDims).map((dim) => [
          dim,
          Object.fromEntries(Object.entries(chips).map(([id, c]) => [id, c.color ?? null])),
        ]),
      )
      shown = attachRecordColors(shown, colorsByDim)
    }
    // no-silent-caps: label resolution beyond the fetch limit is annotated.
    const truncated = Object.entries(kenningsByType).filter(([, k]) => k.total > KENNING_FETCH_LIMIT)
    if (truncated.length > 0) {
      shown = _withWarning(
        shown,
        `Reference labels were resolved for the first ${KENNING_FETCH_LIMIT} records per `
        + `type; groups beyond that render as '${UNNAMED}'.`,
      )
    }
  }
  return shown as T
}

/** Append ONE `incomplete_coverage` warning to a frame's meta. The single
 *  place decorateFrame says "this is not the whole truth" — so the shape of
 *  the annotation cannot drift between the three things that need it. */
function _withWarning<T extends { rows: Record<string, unknown>[]; meta?: any }>(
  frame: T, human: string,
): T {
  return {
    ...frame,
    meta: {
      ...frame.meta,
      warnings: [
        ...(frame.meta?.warnings ?? []),
        { kind: 'incomplete_coverage', human, severity: 'warning' },
      ],
    },
  } as T
}

import { CHIPS_BATCH, fetchChipsInBatches } from '../api/chipBatches'
export { CHIPS_BATCH, fetchChipsInBatches }

/** R7 (GAP B fix round 1): the table/frame fork below decides whether a
 *  request goes out as a `time-bucket` reducer with `rank` on the wire — a
 *  truthy-but-malformed `config.rank` (e.g. `rank: true`) must NOT reach that
 *  path, since `buildReduceRequest` would forward it as-is and the server's
 *  `_Strict` `RankSpec` model 422s. A shape check, not a truthiness check. */
function isRankConfig(rank: unknown): rank is { by: string; n: number; order?: 'asc' | 'desc' } {
  return (
    typeof rank === 'object' && rank !== null
    && typeof (rank as Record<string, unknown>).by === 'string'
    && typeof (rank as Record<string, unknown>).n === 'number'
  )
}

export function ViewHost(props: {
  spec: ViewKindSpec
  slug: string
  typeId: string
  /** roleName → attribute name string (e.g. { group: "building_type" }) */
  roleNames?: Record<string, string>
  entityDef?: string
  measureQuantity?: string
  /** SEN-1513 half 2: the SECOND measure of a repeatable reduce role (line `y`).
   *  Empty/undefined keeps the single-measure wire form. */
  secondMeasure?: string
  agg?: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last'
  granularity?: Granularity
  /** The window; `label` ("fiscal year to date") names it for a caption. */
  period?: { period_start: string; period_end: string; label?: string }
  filter?: unknown
  sort?: unknown
  /** The widget's title — a self_framed renderer draws its own heading from it. */
  title?: string
  /** SEN-699: dim string → target type_id, for dims whose leaf attribute is an
   *  entity-reference. Their group keys (record ids) resolve to kennings. */
  refDims?: Record<string, string>
  config?: Record<string, unknown>
  // Legacy bindings passthrough for table/identity path
  bindings?: Record<string, unknown>
  /** G-IA: when true the table renderer hides its own toolbar (App renders a shared strip). */
  hideToolbar?: boolean
  /** Suppress shared-view writes for a fixed or permission-limited host. */
  readOnly?: boolean
  /** Host-owned reason for rendering a focusable disabled add-column action. */
  viewWritesDisabledReason?: string
  /** G-IA: controlled sort keys passed from App's unified strip to the table. */
  externalSort?: unknown[]
  onSortChange?: (keys: unknown[]) => void
  /** G-IA: controlled filter tree from App's unified strip. */
  externalFilterTree?: unknown
  onFilterTreeChange?: (tree: unknown) => void
  /** Called when the user clicks the underlined primary cell to open the record detail panel. */
  onOpenRecord?: (recordId: string) => void
  /** The table's selection as a SET DESCRIPTION (collection/selection.ts). */
  onSelectionChange?: (selection: SelectionSet) => void
  /** SEN-169: the identity (collection) path's record total, published by the
   *  renderer that already fetched it. Only the collection path supplies it —
   *  a frame renderer has no record universe, so a host that needs the count
   *  under a frame view keeps its own source. */
  onTotal?: (total: number | null) => void
  onReadRequest?: (request: ReduceRequest | null) => void
  /** SEN-1411 (T6/F3): which surface hosts this view — full (Records canvas),
   *  peek (right rail / agent chat), block (dashboard tile / report canvas).
   *  The host enforces the kind's declared DensityPolicy; default full. */
  density?: Density
  /** SEN-1411: agent-suppliable presentation annotations, merged into
   *  frame.meta.marks and rendered at every density. */
  marks?: FrameMark[]
  /** F1 (SEN-1398): the interaction seam for FRAME renderers. When absent, a
   *  default is derived from onOpenRecord: a rowKey that looks like a record id
   *  (dims[0] = site_id group-bys) opens the record peek; category keys no-op. */
  interactions?: import('./registry').ViewInteractions
  /** SEN-703 filtered-to-empty seam (frame path only — the table handles its
   *  own identity path inside CollectionTable): when the frame comes back
   *  with zero rows AND a filter is active, ViewHost renders the shared
   *  FilteredEmptyState instead of the renderer's bare "No data". ONE seam
   *  for every frame renderer — never per-renderer. */
  filterActive?: boolean
  onClearFilter?: () => void
  pluralName?: string
  /** SEN-1424: bound attribute type per role (roleName → attribute type) — see
   *  ViewRendererProps.roleKinds. */
  roleKinds?: Record<string, string>
  /** The bound attribute's config per role — see ViewRendererProps.roleConfigs. */
  roleConfigs?: Record<string, Record<string, unknown>>
  /** SEN-1424: renderer config-write seam (see ViewRendererProps.onConfigChange). */
  onConfigChange?: (patch: Record<string, unknown>) => void
  /** Task 7: record ids CollectionTable should visually mark as pinned/highlighted
   *  (the master filter's pivot record, GAP D). Declared here so Task 5's rank
   *  table and Task 7's pivot table share the same identity-collection shape;
   *  the consumer lands in Task 7. */
  highlightRecordIds?: string[]
  /** The widget's records binding (PR 406 `bind.records`), when the host lets
   *  ViewHost compile its questions. */
  bind?: WidgetBind | null
  /** A10: the questions the host's per-widget assembly already compiled
   *  (widgetQuestions). When given they are asked as-is — the same keys a
   *  prefetch warmed — and the compile props above are not re-read. */
  questions?: WidgetQuestions
  /** §6.3: how long this widget's answers stay fresh and cached — a dashboard
   *  passes its own (DASHBOARD_QUESTION_CACHE), the same one its prefetch
   *  warms with, so a prefetched answer is not asked a second time. */
  cache?: { staleTime: number; gcTime: number }
  /** Owner 2026-10-30 ("the FE just lazily loaded them"): a time-bucket
   *  question is asked for the VISIBLE window only, in chunks of at most
   *  `pointsPerRequest` buckets laid on fixed calendar boundaries (cached per
   *  chunk), inside `period` (the page's window). Absent = one question. */
  lazy?: { visible: TimeWindow; pointsPerRequest: number }
  /** The zoom seam (ViewRendererProps.onZoom), handed to the renderer. */
  onZoom?: (window: TimeWindow) => void
  /** How many of the visible chunks have answered (progressive loading). */
  onProgress?: (progress: { loaded: number; total: number }) => void
}) {
  // SEN-1411: a density's compact variant is itself a registered renderer key —
  // resolve it FIRST so a surface can render even when the full layout renderer
  // isn't loaded (and so "no renderer" is judged against what would actually draw).
  const hostPolicy = densityPolicy(props.spec, props.density ?? 'full')
  const rendererKey = hostPolicy.variant && resolveViewRenderer(hostPolicy.variant)
    ? hostPolicy.variant
    : props.spec.layout_kind
  const Renderer = resolveViewRenderer(rendererKey)
  if (!Renderer) return <div data-testid="view-host-no-renderer">No renderer for {props.spec.layout_kind}</div>

  // Dispatch-identity marker: the RESOLVED registration, readable from the
  // DOM. `data-view-kind` is the spec the host dispatched for, `data-layout`
  // the registry key the resolution walked, `data-renderer` the resolved
  // component's own identity (a swapped registration changes it; the tile
  // heading alone cannot show that — it renders from the widget row, not the
  // renderer). Generic — one wrapper from the SAME resolution every kind
  // walks; display:contents keeps it out of layout. Component names survive
  // the dev server (where the e2e gate runs); a minified build may mangle
  // `data-renderer`, so product code must never dispatch on these attributes.
  const dispatchMarker = {
    'data-view-host': '',
    'data-view-kind': props.spec.kind,
    'data-layout': rendererKey,
    'data-renderer': (Renderer as { displayName?: string }).displayName ?? Renderer.name,
  } as const

  // Input mode belongs to the SELECTED registration, including a compact
  // density variant or the base renderer when that variant is unavailable.
  // A well-shaped rank still requests a frame; malformed rank stays on the
  // collection path (R7).
  if (rendererOptions(rendererKey).needsCollection && !isRankConfig((props.config ?? {}).rank)) {
    return (
      <div style={{ display: 'contents' }} {...dispatchMarker}>
      <ReadRequestContext request={null} onRequest={props.onReadRequest} />
      <Renderer
        mode="identity"
        collection={{
          slug: props.slug,
          typeId: props.typeId,
          filter: props.filter,
          sort: props.sort,
          // SEN-1411 finding 5: the identity path honors the SAME policy axes
          // as the frame path — data budget + interaction budget, not just
          // toolbar chrome.
          maxRows: hostPolicy.max_rows ?? undefined,
          hideToolbar: props.hideToolbar
            || !hostPolicy.chrome.includes('toolbar'),
          readOnly: props.readOnly,
          viewWritesDisabledReason: props.viewWritesDisabledReason,
          externalSort: props.externalSort,
          onSortChange: props.onSortChange,
          externalFilterTree: props.externalFilterTree,
          onFilterTreeChange: props.onFilterTreeChange,
          onOpenRecord: hostPolicy.interactions ? props.onOpenRecord : undefined,
          onSelectionChange: props.onSelectionChange,
          onTotal: props.onTotal,
          // SEN-1731: the strip's resolved period, when the host decided the
          // chip applies (App.tsx `showsPeriod`). The frame path below
          // already threads `props.period` into `buildReduceRequest` — the
          // identity/table path gets the SAME prop, just onto the batch
          // calculated-columns request instead of a reduce scope.
          period: props.period,
          highlightRecordIds: props.highlightRecordIds,
        }}
        bindings={props.bindings as Record<string, unknown>}
        viewKind={props.spec.kind}
        config={props.config}
      />
      </div>
    )
  }

  // The widget's questions: prebuilt by the host's per-widget assembly
  // (widgetQuestions — a dashboard tile, a record widget, the prefetch), or
  // compiled here from the props through the SAME compile (buildQuestions).
  const questions = props.questions ?? buildQuestions({
    spec: props.spec,
    entityDef: props.entityDef ?? '',
    roleNames: props.roleNames ?? {},
    measureQuantity: props.measureQuantity ?? '',
    secondMeasure: props.secondMeasure,
    config: props.config,
    bind: props.bind,
    period: props.period,
    filter: props.filter,
    agg: props.agg,
    granularity: props.granularity,
  })
  const request = questions.reduce ?? null

  // Delegate to inner component that calls useQuery (hooks rules)
  return (
    <div style={{ display: 'contents' }} {...dispatchMarker}>
    <ReadRequestContext request={request} onRequest={props.onReadRequest} />
    <_ViewHostQuery
      spec={props.spec}
      slug={props.slug}
      request={request}
      records={questions.records}
      cache={props.cache}
      lazy={props.lazy}
      onZoom={props.onZoom}
      onProgress={props.onProgress}
      periodLabel={props.period?.label}
      title={props.title}
      onTotal={props.onTotal}
      bindings={props.bindings ?? {}}
      roleNames={props.roleNames ?? {}}
      refDims={props.refDims ?? {}}
      config={{ ...(props.config ?? {}), sortKeys: props.sort }}
      density={props.density ?? 'full'}
      marks={props.marks}
      interactions={props.interactions ?? recordInteractions({ onOpenRecord: props.onOpenRecord })}
      emptyFilter={
        props.filterActive && props.onClearFilter
          ? { pluralName: props.pluralName ?? '', onClear: props.onClearFilter }
          : undefined
      }
      roleKinds={props.roleKinds}
      roleConfigs={props.roleConfigs}
      onConfigChange={props.onConfigChange}
    />
    </div>
  )
}

// Report the actual canonical request built for this view, without rebuilding
// its semantics in the agent UI. Content-keyed so new render objects do not loop.
function ReadRequestContext({ request, onRequest }: { request: ReduceRequest | null; onRequest?: (request: ReduceRequest | null) => void }) {
  const key = JSON.stringify(request)
  useEffect(() => { onRequest?.(JSON.parse(key)) }, [key, onRequest])
  return null
}

// ---------------------------------------------------------------------------
// SEN-1411 (T6/F3): the density contract. The host reads the POLICY — never the
// kind. Density decides chrome and interaction budget, never data semantics;
// budget cuts are truthful (a bitten cap annotates "first N of M").
// PERMISSIVE_FULL covers wire specs from an older backend (densities absent).
// ---------------------------------------------------------------------------
export const PERMISSIVE_FULL: DensityPolicy = {
  chrome: ['toolbar', 'legend', 'axes'],
  max_rows: null, overflow: 'note', interactions: true, variant: null,
}

export function densityPolicy(spec: ViewKindSpec, density: Density): DensityPolicy {
  return spec.densities?.[density] ?? PERMISSIVE_FULL
}

/** R4 (Task 9 fix round 1): a group-by kind's PRIMARY dimension role — the
 *  first REQUIRED, non-measure role (excludes optional roles like `series`,
 *  which partitions a group into more rows without adding a new group; and
 *  excludes `reduce` roles, which are measures, not dimensions). Undefined
 *  for identity/scalar/time-bucket kinds (no grouping to cap by) — those keep
 *  the raw-row cap. `required` alone is the signal available client-side
 *  (the served RoleSpec does not carry a `partitions` flag), and it is
 *  sufficient: every registered group-by kind's series-like roles are
 *  optional (monad/view_kinds_catalog/bar.py, table's frame path has no
 *  roles at all). */
function primaryDimensionRole(spec: ViewKindSpec): string | undefined {
  if (spec.default_reducer !== 'group-by') return undefined
  return spec.roles.find((r) => r.required && !r.reduce)?.name
}

/** Generic data-budget enforcement: cap rows per policy (overflow='note' only —
 *  'scroll' kinds keep every row and scroll) and annotate truthfully.
 *
 *  R4: for a group-by frame with a primary dimension (a cyclic time-part or a
 *  categorical axis — e.g. bar's `group` role), the cap counts DISTINCT
 *  PRIMARY-DIMENSION KEYS, not raw rows. Capping raw rows on a multi-series
 *  frame (weekday x energy source) silently dropped whole trailing
 *  categories (Fri-Sun vanished entirely once 2 sources pushed 14 rows past
 *  an 8-row budget) — the SEN-433 drift class: never silently degrade. */
export function applyDensityBudget<T extends { rows: Record<string, unknown>[]; meta: { warnings?: unknown[] | null } }>(
  frame: T, policy: DensityPolicy, spec?: ViewKindSpec,
): T {
  if (policy.max_rows == null || policy.overflow !== 'note') return frame
  const primaryKey = spec ? primaryDimensionRole(spec) : undefined
  if (!primaryKey) {
    if (frame.rows.length <= policy.max_rows) return frame
    const total = frame.rows.length
    return {
      ...frame,
      rows: frame.rows.slice(0, policy.max_rows),
      meta: {
        ...frame.meta,
        warnings: [
          ...(frame.meta.warnings ?? []),
          {
            kind: 'incomplete_coverage',
            human: `Showing first ${policy.max_rows} of ${total} rows — open the full view for the rest.`,
            severity: 'info',
          },
        ],
      },
    }
  }
  const groupOrder: string[] = []
  const seenGroups = new Set<string>()
  let anyRowCarriesKey = false
  for (const row of frame.rows) {
    if (primaryKey in row) anyRowCarriesKey = true
    const key = String(row[primaryKey] ?? '')
    if (!seenGroups.has(key)) { seenGroups.add(key); groupOrder.push(key) }
  }
  if (!anyRowCarriesKey) {
    // M8/L51d: the primary role key is declared but NO row carries it — every
    // row degenerately mapped to the same '' group key, which would silently
    // UNCAP the frame (`groupOrder.length === 1 <= max_rows`) instead of
    // applying the raw-row cap the pre-group-cap behaviour guaranteed. Fall
    // back to that raw-row cap, annotated as a method downgrade (not the
    // info-tone 'incomplete_coverage' a real group cap uses) so the reader
    // sees WHY grouping did not apply.
    if (frame.rows.length <= policy.max_rows) return frame
    const total = frame.rows.length
    return {
      ...frame,
      rows: frame.rows.slice(0, policy.max_rows),
      meta: {
        ...frame.meta,
        warnings: [
          ...(frame.meta.warnings ?? []),
          {
            kind: 'method_downgrade',
            human: `No row carries the group-by dimension; showing the first ${policy.max_rows} of ${total} rows instead — open the full view for the rest.`,
            severity: 'warning',
          },
        ],
      },
    }
  }
  if (groupOrder.length <= policy.max_rows) return frame
  const totalGroups = groupOrder.length
  const kept = new Set(groupOrder.slice(0, policy.max_rows))
  return {
    ...frame,
    rows: frame.rows.filter((row) => kept.has(String(row[primaryKey] ?? ''))),
    meta: {
      ...frame.meta,
      warnings: [
        ...(frame.meta.warnings ?? []),
        {
          kind: 'incomplete_coverage',
          human: `Showing first ${policy.max_rows} of ${totalGroups} groups — open the full view for the rest.`,
          severity: 'info',
        },
      ],
    },
  }
}

/** SEN-1444: the marks gate. Drops marks the kind's declared marks_supported
 *  cannot draw, WITH a dev-console warning naming the dropped kinds — never a
 *  silent visual drop (truthful affordances). Defense in depth behind the
 *  server-side seam (monad/poiesis/marks.py::enforce_marks_for_kind). A spec
 *  without marks_supported (older backend on the wire) is permissive —
 *  mirrors PERMISSIVE_FULL for absent densities. */
export function gateMarks(marks: FrameMark[], spec: ViewKindSpec): FrameMark[] {
  const supported = spec.marks_supported
  if (supported == null) return marks
  const kept = marks.filter((m) => supported.includes(m.kind))
  const droppedKinds = [...new Set(
    marks.filter((m) => !supported.includes(m.kind)).map((m) => m.kind),
  )]
  if (droppedKinds.length > 0) {
    console.warn(
      `[poiesis] mark kind(s) ${droppedKinds.join(', ')} are not renderable by `
      + `view kind '${spec.kind}' (marks_supported: ${supported.join(', ') || 'none'}) — dropped`,
    )
  }
  return kept
}

/** rowKey that looks like a record id (site_id group-bys) — see ViewInteractions. */
const _UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** A host's interaction seam from its two callbacks (A17): a selected row
 *  whose key is a record id opens the record; a drill passes the picks
 *  through. ONE copy of the record-id rule for every host. */
export function recordInteractions({ onOpenRecord, onDrill }: {
  onOpenRecord?: (recordId: string) => void
  onDrill?: (picks: Record<string, string>) => void
}): import('./registry').ViewInteractions | undefined {
  if (!onOpenRecord && !onDrill) return undefined
  return {
    ...(onOpenRecord ? { onSelect: (rowKey: string) => { if (_UUIDISH.test(rowKey)) onOpenRecord(rowKey) } } : {}),
    ...(onDrill ? { onDrill } : {}),
  }
}

/** The frame a widget that asks no reduce question (a records-only stat) is
 *  handed: no rows, nothing measured — its answer is in `groups`. */
function noReduceFrame(): ReducedFrame {
  return {
    rows: [],
    meta: { dimensions: [], unit_composition: { numerator: [], denominator: [] }, warnings: [], dropped: [],
      accurate_at: '', mrl_snapshot_id: '' },
  } as unknown as ReducedFrame
}

function _ViewHostQuery(props: {
  spec: ViewKindSpec
  slug: string
  request: ReduceRequest | null
  records?: WidgetQuestions['records']
  cache?: { staleTime: number; gcTime: number }
  lazy?: { visible: TimeWindow; pointsPerRequest: number }
  onZoom?: (window: TimeWindow) => void
  onProgress?: (progress: { loaded: number; total: number }) => void
  periodLabel?: string
  title?: string
  onTotal?: (total: number | null) => void
  bindings: Record<string, unknown>
  roleNames: Record<string, string>
  refDims: Record<string, string>
  config?: Record<string, unknown>
  density: Density
  marks?: FrameMark[]
  interactions?: import('./registry').ViewInteractions
  emptyFilter?: { pluralName: string; onClear: () => void }
  roleKinds?: Record<string, string>
  roleConfigs?: Record<string, Record<string, unknown>>
  onConfigChange?: (patch: Record<string, unknown>) => void
}) {
  // SEN-1411: the policy picks the renderer — a compact variant is a REGISTERED
  // renderer key (e.g. kanban peek → 'column-counts'), same registry as layout
  // kinds. Falls back to the layout kind if a variant isn't registered (the
  // conformance guard keeps the vocabulary and the registry in lockstep).
  const policy = densityPolicy(props.spec, props.density)
  const Renderer = (policy.variant ? resolveViewRenderer(policy.variant) : null)
    ?? resolveViewRenderer(props.spec.layout_kind)!
  // Owner 2026-10-30: a lazy time-bucket question is the visible window's
  // chunks — one engine question each, under the SAME query key family, so a
  // chunk asked once (another zoom, a pan back) is a cache hit.
  const lazyScope = props.request?.scope
  const lazyChunks = props.lazy && props.request?.reducer.kind === 'time-bucket'
    && lazyScope?.period_start && lazyScope.period_end
    ? chunkWindows(props.lazy.visible, { period_start: lazyScope.period_start, period_end: lazyScope.period_end },
        props.request.reducer.granularity, props.lazy.pointsPerRequest,
        props.request.reducer.tz ?? getTenantZone())
    : null
  const lazyGrain = props.request?.reducer.kind === 'time-bucket' ? props.request.reducer.granularity : ''
  const chunkQueries = useQueries({
    queries: (lazyChunks ?? []).map((w) => {
      const r = chunkRequest(props.request!, w)
      return { queryKey: ['reduce', props.slug, r], queryFn: () => reduce(props.slug, r), ...props.cache }
    }),
  })
  const reduceQuery = useQuery({
    queryKey: ['reduce', props.slug, props.request],
    enabled: props.request !== null && lazyChunks === null,
    queryFn: () => reduce(props.slug, props.request!),
    ...props.cache,
  })
  // progressive: the chunks that answered draw at once (in order — a chunk
  // never splits a bucket); until the first of a new window answers, the last
  // drawn frame stays on screen, never a blank wait
  const loadedChunks = chunkQueries.flatMap((q) => (q.data ? [q.data as ReducedFrame] : []))
  const merged = lazyChunks ? mergeChunkFrames(loadedChunks) : undefined
  const lastLazy = useRef<ReducedFrame | undefined>(undefined)
  if (merged && loadedChunks.length === chunkQueries.length) lastLazy.current = merged
  const lazyFrame = merged ?? lastLazy.current
  const chunkError = chunkQueries.find((q) => q.isError)?.error
  const progressKey = lazyChunks ? `${loadedChunks.length}/${chunkQueries.length}` : ''
  const onProgress = props.onProgress
  useEffect(() => {
    if (!progressKey) return
    const [loaded, total] = progressKey.split('/').map(Number)
    onProgress?.({ loaded: loaded!, total: total! })
  }, [progressKey, onProgress])
  const recordsQuery = useQuery({
    queryKey: ['records-agg', props.slug, props.records ?? null],
    enabled: props.records !== undefined,
    queryFn: () => monadClient.askRecords(props.slug, props.records!.object, props.records!.query),
    ...props.cache,
  })
  const groups: RecordGroup[] | undefined = recordsQuery.data
  const asked = { reduce: props.request !== null, records: props.records !== undefined }
  const frame = lazyChunks
    ? (lazyFrame && props.lazy ? visibleRows(lazyFrame, props.lazy.visible, lazyGrain) : undefined)
    : props.request !== null ? reduceQuery.data : (props.records ? noReduceFrame() : undefined)
  const isLoading = (props.request !== null && (lazyChunks
    ? !lazyFrame && lazyChunks.length > 0 && !chunkError
    : reduceQuery.isLoading)) || (props.records !== undefined && recordsQuery.isLoading)
  const isError = (lazyChunks ? Boolean(chunkError) && !lazyFrame : reduceQuery.isError) || recordsQuery.isError
  const error = (lazyChunks ? chunkError : reduceQuery.error) ?? recordsQuery.error

  // ask(): an extra question under THIS widget's scope and period, compiled by
  // the one compile from the widget's own reduce scope and run through the
  // same reduce + batch path under the same query keys (a reopened popup is a
  // cache hit). The renderer never calls the API itself.
  const queryClient = useQueryClient()
  const baseScope = props.request?.scope
  const ask = useCallback((q: AskQuestion): Promise<ReducedFrame> => {
    const r = buildQuestions({
      spec: props.spec,
      entityDef: baseScope?.entity_def ?? props.records?.object ?? '',
      period: baseScope?.period_start && baseScope?.period_end
        ? { period_start: baseScope.period_start, period_end: baseScope.period_end } : undefined,
      filter: composeFilter(baseScope?.filter ?? props.records?.query.filter ?? undefined, q.filter as WireTree | undefined),
      config: q.compare ? { compare: q.compare } : {},
      measures: q.measures,
      measuresUnit: q.unit,
    }).reduce!
    return queryClient.fetchQuery({ queryKey: ['reduce', props.slug, r], queryFn: () => reduce(props.slug, r) })
  }, [queryClient, props.slug, props.spec, baseScope, props.records])

  // SEN-699: kennings for every reference-dim target type (one MRL per type).
  const targetTypeIds = [...new Set(Object.values(props.refDims))].sort()
  const { data: kenningsByType, isLoading: kenningsLoading } = useQuery({
    queryKey: ['kennings', props.slug, targetTypeIds],
    enabled: targetTypeIds.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        targetTypeIds.map(async (tid) => {
          const mrl = await monadClient.getMrl(props.slug, tid, KENNING_FETCH_LIMIT, 0)
          return [tid, { kennings: mrl.record_kennings ?? {}, total: mrl.total_count }] as const
        }),
      )
      return Object.fromEntries(entries) as Record<string, { kennings: Record<string, string>; total: number }>
    },
  })

  // Record colours for reference dims (2026-09-02): the referenced records'
  // chips — the same chip seam every surface uses — so a series bound to an
  // energy source draws in the source's OWN colour. Keyed on the ids actually
  // present in this frame, so it costs one request per frame and nothing for
  // frames without reference dims.
  // Ids to resolve to chips: the reference dims' values AND every id inside
  // a set-valued column (SEN-1733 — the frame names them in meta.set_columns).
  const setColumns = frame?.meta?.set_columns ?? []
  const identityColumns = props.spec.roles.some(
    (role) => role.decorates_identity && props.roleNames?.[role.name],
  ) ? ['entity_ref'] : []
  // The records question's group keys that are references (a group_by path
  // bound to a reference dim): their records get the same chip seam — the
  // item list's flag is the group record's identity icon.
  const groupRefIds = (dim: string): string[] => {
    const i = props.records?.query.group_by?.indexOf(dim) ?? -1
    return i < 0 ? [] : (groups ?? []).map((g) => g.key[i]).filter((v): v is string => typeof v === 'string')
  }
  const frameRows = frame?.rows ?? []
  const refIdsInFrame = frame || groups
    ? [...new Set([
        ...Object.keys(props.refDims).flatMap((dim) => [
          ...frameRows.map((r) => r[dim]).filter((v): v is string => typeof v === 'string'),
          ...groupRefIds(dim)]),
        ...setColumns.flatMap((col) =>
          frameRows.flatMap((r) => (Array.isArray(r[col]) ? (r[col] as unknown[]).map(String) : []))),
        ...(identityColumns.length > 0
          ? frameRows.map((row) => row.entity_ref).filter((value): value is string => typeof value === 'string')
          : []),
      ])].sort()
    : []
  // cold read 4: chunked to the server's cap, and the FAILURE is read — an
  // unreadable chip request must annotate the frame, never silently turn every
  // stacked marker into a plain dot.
  const { data: chipFetch, isError: chipQueryFailed } = useQuery({
    queryKey: ['chips', props.slug, refIdsInFrame],
    enabled: refIdsInFrame.length > 0,
    queryFn: () => fetchChipsInBatches(props.slug, refIdsInFrame),
  })
  const chips = chipFetch?.chips

  // SEN-433: a TEMPLATE option over a role's record cells (OptionField.
  // template_of — the item list's `label: "{kenning} - {name}"`): the named
  // cells other than the kenning are read with ONE records/values for the rows'
  // records. Generic: the template's {names} are the attributes asked for.
  const templateRead = (() => {
    for (const opt of props.spec.config_schema ?? []) {
      if (!opt.template_of) continue
      const template = (props.config ?? {})[opt.key] ?? opt.default
      const names = typeof template === 'string'
        ? [...new Set([...template.matchAll(/\{([A-Za-z0-9_]+)\}/g)].map((m) => m[1]!).filter((n) => n !== 'kenning'))]
        : []
      const dim = props.roleNames[opt.template_of]
      const target = dim ? props.refDims[dim] : undefined
      if (!names.length || !dim || !target) continue
      const ids = [...new Set([...frameRows.map((r) => r[dim]).filter((v): v is string => typeof v === 'string'),
        ...groupRefIds(dim)])].sort()
      return ids.length ? { target, ids, names } : null
    }
    return null
  })()
  // records/values answers each record's cells by ATTRIBUTE ID; the template names them by slug — the
  // target type's attributes (the metadata seam's cached read) say which id each name is
  const { data: templateAttrs } = useQuery({ ...attributesQuery(props.slug, templateRead?.target ?? ''),
    enabled: templateRead !== null })
  const { data: templateValues } = useQuery({
    queryKey: ['template-cells', props.slug, templateRead],
    enabled: templateRead !== null,
    queryFn: async () => (await monadClient.getValues(props.slug, templateRead!.target, templateRead!.ids, templateRead!.names)).values,
  })
  const templateCells = templateValues && templateAttrs ? cellsBySlug(templateValues, templateAttrs) : undefined
  // a batch that failed (or the whole query) is annotated, never a silent dot
  const chipsFailed = chipQueryFailed || (chipFetch?.failedBatches ?? 0) > 0

  const windowOf = (() => {
    const w = baseScope?.period_start && baseScope?.period_end
      ? { period_start: baseScope.period_start, period_end: baseScope.period_end }
      : props.records?.query.period_start && props.records.query.period_end
        ? { period_start: props.records.query.period_start, period_end: props.records.query.period_end }
        : undefined
    // a lazy chart reads the VISIBLE window (its coverage note is about it)
    const shownWindow = lazyChunks && props.lazy && w ? props.lazy.visible : w
    return shownWindow ? { ...shownWindow, ...(props.periodLabel ? { label: props.periodLabel } : {}) } : undefined
  })()
  // SEN-433: a kind that draws its own pending/refused state (renders_pending,
  // the KPI card) is mounted while loading and when refused — its label and
  // icon stay on screen. Read from the registry row, never the kind's name.
  if (props.spec.renders_pending && (isLoading || isError)) {
    return (
      <Renderer mode="frame" frame={noReduceFrame()} bindings={props.bindings} roleNames={props.roleNames}
        viewKind={props.spec.kind} config={props.config} density={props.density} chrome={policy.chrome}
        roleKinds={props.roleKinds} roleConfigs={props.roleConfigs} period={windowOf} title={props.title}
        asked={asked} groups={groups} onTotal={props.onTotal}
        pending={isLoading && !isError} error={isError ? error : undefined} />
    )
  }
  if (isLoading || (targetTypeIds.length > 0 && kenningsLoading)) {
    return <div data-testid="view-loading">Loading…</div>
  }
  if (isError) return <div data-testid="view-error">{String((error as Error)?.message ?? 'Error')}</div>
  if (!frame) return null

  let shown = decorateFrame(frame, {
    refDims: props.refDims,
    setColumns,
    chips,
    kenningsByType: targetTypeIds.length > 0 ? kenningsByType : undefined,
    chipsFailed: refIdsInFrame.length > 0 && chipsFailed,
    identityColumns,
  })

  // SEN-1410: translate slug-keyed identity rows into the renderers'
  // role-keyed contract — AFTER the reference remap, so role keys carry
  // resolved kenning labels, not raw ids.
  shown = remapRoleColumns(shown, props.spec, props.roleNames)

  // SEN-1411 (T6/F3): data budget + agent marks. Budget bites AFTER remaps so
  // the truthful "first N of M" counts rendered rows; marks merge into meta so
  // FrameAnnotations and mark-aware renderers see them at every density.
  // SEN-1444: ALL marks (server-sent meta.marks + host/agent props.marks) pass
  // the gate against the kind's declared marks_supported — defense in depth
  // behind the server-side seam (monad/poiesis/marks.py), so a stale client
  // or server never silently drops: undeclared marks warn on the dev console.
  shown = applyDensityBudget(shown, policy, props.spec)
  const allMarks = [...(shown.meta.marks ?? []), ...(props.marks ?? [])]
  if (allMarks.length > 0) {
    shown = {
      ...shown,
      meta: { ...shown.meta, marks: gateMarks(allMarks, props.spec) },
    } as typeof shown
  }

  // SEN-703: filtered-to-empty. Empty frames still carry their WHY
  // (meta.warnings / meta.dropped) — keep the annotations visible.
  if (shown.rows.length === 0 && props.emptyFilter) {
    return (
      <FilteredEmptyState pluralName={props.emptyFilter.pluralName} onClear={props.emptyFilter.onClear}>
        <FrameAnnotations meta={shown.meta} />
      </FilteredEmptyState>
    )
  }

  // M9 (SEN-730): expose each role's served `consumes_subfields` so a frame
  // renderer can reconstruct the exact composite columns buildReduceRequest
  // emitted ("<slug>.<sub>"), without re-deriving the convention or guessing.
  // Generic over roles — no per-kind branch.
  const roleSubfields: Record<string, string[]> = Object.fromEntries(
    props.spec.roles
      .filter((r) => r.consumes_subfields && r.consumes_subfields.length > 0)
      .map((r) => [r.name, r.consumes_subfields as string[]]),
  )

  // A reduce role nobody bound by name reads the SCOPE quantity on an identity
  // frame — that is the column the request above asked for (a fabricated map
  // view carries measureQuantity, not roleNames.measure). Aggregate frames key
  // their measure as `value`, so only identity frames are completed this way.
  const roleNamesForRenderer = (() => {
    const q = props.request?.scope.quantity
    if (props.request?.reducer.kind !== 'identity' || !q) return props.roleNames
    const filled = Object.fromEntries(
      props.spec.roles.filter((r) => r.reduce && !props.roleNames?.[r.name]).map((r) => [r.name, q]),
    )
    return Object.keys(filled).length ? { ...(props.roleNames ?? {}), ...filled } : props.roleNames
  })()

  return (
    <Renderer
      mode="frame"
      frame={shown}
      asked={asked}
      groups={groups}
      groupBy={props.records?.query.group_by ?? undefined}
      chips={chips}
      cells={templateCells}
      onTotal={props.onTotal}
      edgeToEdge={policy.edge_to_edge}
      ask={ask}
      bindings={props.bindings}
      roleNames={roleNamesForRenderer}
      roleSubfields={roleSubfields}
      axisBaseline={props.spec.axis_baseline}
      viewKind={props.spec.kind}
      config={props.config}
      density={props.density}
      chrome={policy.chrome}
      interactions={policy.interactions ? props.interactions : undefined}
      roleKinds={props.roleKinds}
      roleConfigs={props.roleConfigs}
      onConfigChange={props.onConfigChange}
      period={windowOf}
      title={props.title}
      onZoom={props.onZoom}
    />
  )
}
